import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import Navbar from './components/Navbar';
import Sidebar from './components/Sidebar';
import TravelQuotes from './components/TravelQuotes';
import ErrorBoundary from './components/ErrorBoundary';
import LoadingSpinner from './components/LoadingSpinner';
import useAuth from './hooks/useAuth';
import { setUser, setUserRole } from './store/store';

// Pages
import HomePage from './pages/HomePage';
import LoginPage from './pages/LoginPage';
import SignupPage from './pages/SignupPage';
import SearchPage from './pages/SearchPage';
import SeatSelectionPage from './pages/SeatSelectionPage';
import BookingsPage from './pages/BookingsPage';
import AdminDashboard from './pages/AdminDashboard';
import PassengerDashboard from './pages/PassengerDashboard';
import TTRDashboard from './pages/TTRDashboard';
import ProfilePage from './pages/ProfilePage';
import ManageUsersPage from './pages/ManageUsersPage';
import ManageTrainsPage from './pages/ManageTrainsPage';
import NotFoundPage from './pages/NotFoundPage';
import UnauthorizedPage from './pages/UnauthorizedPage';

// Route Guards
const ProtectedRoute = ({ children, requiredRole = null, requireAuth = true }) => {
  const { user, userRole, loading } = useAuth();

  if (loading) {
    return <LoadingSpinner />;
  }

  if (requireAuth && !user) {
    return <Navigate to="/login" />;
  }

  if (requiredRole && user && userRole !== requiredRole) {
    return <UnauthorizedPage />;
  }

  return children;
};

const MainLayout = ({ children }) => {
  const { user } = useAuth();

  if (!user) {
    return children;
  }

  return (
    <div className="flex">
      <Sidebar />
      <div className="flex-1">
        {children}
      </div>
    </div>
  );
};

function App() {
  const { user, userRole, loading } = useAuth();
  const dispatch = useDispatch();
  const [appLoading, setAppLoading] = useState(true);

  useEffect(() => {
    if (!loading) {
      dispatch(setUser(user));
      dispatch(setUserRole(userRole));
      setAppLoading(false);
    }
  }, [user, userRole, loading, dispatch]);

  if (appLoading) {
    return <LoadingSpinner />;
  }

  return (
    <ErrorBoundary>
      <Router>
        <Navbar />
        <MainLayout>
          <Routes>
            {/* Public Routes */}
            <Route path="/" element={<HomePage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/signup" element={<SignupPage />} />

            {/* Protected Routes - All Users */}
            <Route
              path="/search"
              element={
                <ProtectedRoute>
                  <SearchPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/seat-selection/:trainId"
              element={
                <ProtectedRoute>
                  <SeatSelectionPage />
                </ProtectedRoute>
              }
            />

            {/* Passenger Routes */}
            <Route
              path="/passenger-dashboard"
              element={
                <ProtectedRoute requiredRole="passenger">
                  <PassengerDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/bookings"
              element={
                <ProtectedRoute requiredRole="passenger">
                  <BookingsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/profile"
              element={
                <ProtectedRoute>
                  <ProfilePage />
                </ProtectedRoute>
              }
            />

            {/* TTR Routes */}
            <Route
              path="/ttr-dashboard"
              element={
                <ProtectedRoute requiredRole="ttr">
                  <TTRDashboard />
                </ProtectedRoute>
              }
            />

            {/* Admin Routes */}
            <Route
              path="/admin-dashboard"
              element={
                <ProtectedRoute requiredRole="admin">
                  <AdminDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/manage-users"
              element={
                <ProtectedRoute requiredRole="admin">
                  <ManageUsersPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/manage-trains"
              element={
                <ProtectedRoute requiredRole="admin">
                  <ManageTrainsPage />
                </ProtectedRoute>
              }
            />

            {/* Error Routes */}
            <Route path="/unauthorized" element={<UnauthorizedPage />} />
            <Route path="/404" element={<NotFoundPage />} />
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </MainLayout>
        <TravelQuotes />
      </Router>
    </ErrorBoundary>
  );
}

export default App;
